/*
  Generic drawer opener + selection tracking for PDP `pdp_option_drawer` /
  `pdp_quick_link_drawer` blocks. One shared implementation instead of a
  bespoke handler per drawer instance.
*/
window.PDPDrawers = (function () {
  const selectedUpsells = {};

  const MAIN_PRICE_SELECTORS = [
    '[data-update-block="price-compact-main"] [data-base-price-cents]',
    '[data-update-block="price-extended-main"] [data-base-price-cents]',
    '[data-update-block="price-sticky-main"] [data-base-price-cents]',
    '.sticky-add-to-cart__price-original[data-base-price-cents]',
    '.button__price[data-base-price-cents]',
  ].join(', ');

  function formatMoney(cents, format) {
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) {
        return 0;
      }

      if (precision > 0 && number % 100 === 0) {
        precision = 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount + KROWN.settings.iso_code;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  // Renames the PDP title to match the selected Performance Technology (rules
  // in product-3d-configurator-map.js's productTitleByMonitor). Only the title
  // text node is touched - the wishlist button sits inside the same element.
  // The page's own title is remembered on first run so switching back to S4
  // restores it exactly.
  function updateProductTitle() {
    const rules = window.ProductConfiguratorMap && window.ProductConfiguratorMap.productTitleByMonitor;
    if (!rules) return;
    const titleEl = document.querySelector('.product__subtitle--with-wishlist, .product__title--with-wishlist');
    if (!titleEl) return;
    // The rower templates wrap the title as <h4>{{ product.title }}</h4> inside
    // the text block, so the text node is nested, not a direct child.
    const walker = document.createTreeWalker(titleEl, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => (node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
    });
    const textNode = walker.nextNode();
    if (!textNode) return;
    if (titleEl.dataset.baseTitle === undefined) titleEl.dataset.baseTitle = textNode.textContent.trim();

    const monitor = Object.values(selectedUpsells)
      .flat()
      .find((item) => item.ldFeatureId === 'monitor');
    const rule = monitor && rules[monitor.ldVariantId];
    const words = titleEl.dataset.baseTitle.split(/\s+/);
    let title = titleEl.dataset.baseTitle;
    if (rule && rule.insert) title = [...words.slice(0, 2), rule.insert, ...words.slice(2)].join(' ');
    else if (rule && rule.append) title = `${title} ${rule.append}`;
    textNode.textContent = title;
  }

  function updateMainPrice() {
    // priceCarriedByVariant items (see update() below) must NOT also add
    // their price here - their cost is already inside data-base-price-cents
    // once the real variant swap (syncHiddenVariantOption's dispatched
    // 'change') resolves. Confirmed live 2026-09-07 on WaterRower Original:
    // its base variant is already priced per Performance Technology value
    // (S4 Bluetooth £1,199 / PowerPulley £1,449 / CityRow Touchscreen
    // £1,599, matching the drawer's own advertised +£250/+£400 exactly) -
    // adding PowerPulley's price again on top double-charged the customer.
    const accessoryCents = Object.values(selectedUpsells)
      .flat()
      .filter((item) => !item.priceCarriedByVariant)
      .reduce((sum, item) => sum + (item.priceCents || 0), 0);

    document.querySelectorAll(MAIN_PRICE_SELECTORS).forEach((elm) => {
      const baseCents = Number(elm.dataset.basePriceCents) || 0;
      elm.textContent = formatMoney(baseCents + accessoryCents, window.KROWN.settings.shop_money_format);
    });
  }

  // Scoped to the real PDP only — pdp_option_drawer blocks don't currently
  // render anywhere else, but this keeps quick-view/quick-buy safe if that
  // ever changes (both those templates reuse the same <product-page>
  // wrapper markup, so history writes there would otherwise pollute the
  // underlying page's own URL). Was previously keyed off
  // [data-main-product-page-variants], the <product-variants> element's own
  // marker — but that element (and its marker) only renders when the
  // product actually has a native variant picker to show. WaterRower
  // Metallic has none (every option is driven entirely through drawers/AR,
  // no visible or hidden picker at all), so that check silently disabled
  // ?upsell= restore and write entirely on that product - confirmed live
  // 2026-09-09. data-main-product-page is a dedicated marker on
  // sections/main-product.liquid's own <product-page> wrapper instead,
  // unconditional regardless of whether a variant picker renders.
  function isMainProductPage() {
    return !!document.querySelector('[data-main-product-page]');
  }

  // Mirrors component-product-form.js's own #updateURL, merging via
  // URLSearchParams rather than overwriting the query string outright so
  // neither this nor the variant picker's own URL write clobbers the other.
  //
  // Each entry is the item's own Shopify id, plus - only when the item
  // carries one - its ld_feature_id:ld_variant_id appended after a "|". That
  // second half is what lets restoreFromURL() find a colour-matched
  // duplicate product (eg. Shadow's own "CityRow Connect Touchscreen
  // Shadow") on whatever page a wood-swap navigation or shared link lands
  // on, since that product's own Shopify id never matches the one this URL
  // was generated with. Plain accessories with no ld mapping (Grips, Floor
  // Mat) keep the exact same bare-id entries as before.
  function updateURL() {
    if (!isMainProductPage()) return;
    const entries = Object.values(selectedUpsells)
      .flat()
      .map((item) => (item.ldFeatureId && item.ldVariantId ? `${item.id}|${item.ldFeatureId}:${item.ldVariantId}` : item.id));

    const url = new URL(window.location.href);
    if (entries.length > 0) {
      url.searchParams.set('upsell', entries.join(','));
    } else {
      url.searchParams.delete('upsell');
    }
    window.history.replaceState({}, '', url);
  }

  // Companion to updateURL(): finds which drawer template a given accessory
  // variant id belongs to (searching each <template>'s inert content, since
  // drawers are only cloned into the live DOM the first time they're
  // opened — see getOrCreateDrawer), force-creates that drawer, then checks
  // the matching input and dispatches a real 'change' event. Going through a
  // real event (rather than just setting .checked) is what keeps this in
  // sync with everything else that already listens for it: this module's own
  // price/summary update(), and the AR/3D scene sync in
  // component-product-ar-launcher.js — same pattern already used by the
  // data-js-select-input click handler below.
  //
  // Tries an exact id match first (works for every genuinely shared
  // product - both other Performance Tech options, every Accessories item).
  // Falls back to the entry's own ld_feature_id:ld_variant_id (see
  // updateURL() above) when the id itself isn't found on this page at all -
  // the case where the wood colour this link/swatch-navigation landed on
  // has its own separate, colour-matched product for the same scene pick.
  function restoreFromURL() {
    if (!isMainProductPage()) return;
    const raw = new URLSearchParams(window.location.search).get('upsell');
    if (!raw) return;

    raw
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .forEach((entry) => {
        const [idPart, ldPart] = entry.split('|');
        const id = idPart.trim();
        if (!/^\d+$/.test(id)) return;
        const [ldFeatureId, ldVariantId] = ldPart ? ldPart.split(':').map((v) => v.trim()) : [];

        const templates = Array.from(document.querySelectorAll('template[data-pdp-drawer]'));
        let template = templates.find((tpl) => tpl.content.querySelector(`input[value="${id}"]`));
        let selector = `input[value="${id}"]`;

        if (!template && ldFeatureId && ldVariantId) {
          selector = `input[data-ld-feature-id="${ldFeatureId}"][data-ld-variant-id="${ldVariantId}"]`;
          template = templates.find((tpl) => tpl.content.querySelector(selector));
        }
        if (!template) return;

        const drawer = getOrCreateDrawer(template.dataset.pdpDrawer);
        const input = drawer && drawer.querySelector(selector);
        if (!input || input.checked) return;

        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
  }

  // Finds a pdp_option_drawer input by its ld_feature_id/ld_variant_id
  // (rather than its Shopify id), force-creates that drawer, and checks it
  // (dispatching a real 'change') - the same "search every drawer template's
  // inert content" approach restoreFromURL() already uses for its own
  // colour-matched-duplicate fallback, factored out here so
  // restoreFromReadableParams() below can reuse it without touching that
  // already-twice-fixed function's own logic.
  function selectDrawerInputByFeature(featureId, variantId) {
    if (!featureId || !variantId) return;
    const selector = `input[data-ld-feature-id="${featureId}"][data-ld-variant-id="${variantId}"]`;
    const templates = Array.from(document.querySelectorAll('template[data-pdp-drawer]'));
    const template = templates.find((tpl) => tpl.content.querySelector(selector));
    if (!template) return;

    const drawer = getOrCreateDrawer(template.dataset.pdpDrawer);
    const input = drawer && drawer.querySelector(selector);
    if (!input || input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Human-readable alternative to ?upsell= for links authored outside this
  // codebase - paid ad landing pages and internal QA links, where someone
  // hand-types or pastes the URL and opaque numeric ids/pipe-delimited
  // ld_feature_id:ld_variant_id pairs are hard to author or verify.
  // product-3d-configurator-map.js's urlDeepLinkParams maps a plain query
  // param name (eg. "tech") to the scene feature it drives plus a
  // slug -> ld_variant_id table (eg. "powerpulley" -> "power-pulley"),
  // curated to match the client's own naming rather than reusing internal
  // ids verbatim. Reuses selectDrawerInputByFeature() above, so a slug
  // resolves to whichever wood-colour's own (possibly duplicate) product
  // this page actually needs, same as the ?upsell= fallback does.
  //
  // A slug that isn't recognised, or whose feature has no drawer item
  // needing selection (eg. rails=standard - already the page's own default,
  // with no "off" item in the drawer to check), is a harmless no-op; this
  // never throws or blocks the rest of page init on a bad/unexpected param.
  function restoreFromReadableParams() {
    if (!isMainProductPage()) return;
    const map = window.ProductConfiguratorMap;
    const params = map && map.urlDeepLinkParams;
    if (!params) return;

    const search = new URLSearchParams(window.location.search);
    Object.keys(params).forEach((paramName) => {
      const slug = (search.get(paramName) || '').trim().toLowerCase();
      if (!slug) return;
      const { featureId, values } = params[paramName];
      const variantId = values && values[slug];
      selectDrawerInputByFeature(featureId, variantId);
    });
  }

  // Reverse of component-product-ar-launcher.js's variantIdByOptionValue
  // lookup: given a scene feature/variant ID, find which real Shopify
  // option value it corresponds to, and check the matching hidden picker
  // input (rendered per-value for every hidden option in
  // sections/main-product.liquid). Shared by syncHiddenVariantOption (a
  // drawer pick just claimed this feature) and revertHiddenVariantOption (a
  // drawer pick just stopped claiming it) below - both only need "make this
  // feature's hidden option reflect this variant ID", they just resolve
  // which variant ID differently.
  //
  // Dispatches a real 'change' event when that flips the hidden input's
  // checked state, so component-product-form.js's own variant-matching
  // fetch runs immediately and the *actual* Shopify variant added to cart
  // (input[name="id"]) is swapped to match - eg. picking PowerPulley (a
  // "Performance Technology" value per product-3d-configurator-map.js,
  // mutually exclusive with S4 Bluetooth on the same "monitor" scene
  // feature) needs the base rower's own variant to become its PowerPulley
  // variant, not stay on whatever the page loaded with. An earlier version
  // of this function deliberately skipped the dispatch, relying on some
  // *other*, real option change to come along later and pick this up for
  // free - but if the shopper never touches another option (the common
  // case: they configure Performance Tech, then hit Add to Cart directly),
  // that later change never happens and the wrong variant ships. The
  // `!hiddenOption.checked` guard keeps this a no-op on every call where
  // the hidden option already matches (eg. re-running the same default on
  // drawer re-init), so it only ever fires a genuinely corrective fetch.
  // Known gap: doesn't resolve variantIdAliasBySku, so a drawer item shared
  // across scenes (see that map in product-3d-configurator-map.js) whose
  // ld_variant_id is still the semantic alias, not the resolved scene id,
  // won't match here — falls back to leaving the hidden inputs as they are.
  //
  // Returns whether this product actually HAS a matching hidden variant
  // option for the pick (true even when it was already checked and nothing
  // needed to change) - update() uses that to mark the pick
  // priceCarriedByVariant, since a real, priced Shopify variant and this
  // drawer's own separate line item must never both bill for the same
  // pick (see updateMainPrice()/getSelectedUpsellItems() for why).
  function applyHiddenVariantOption(featureId, variantId) {
    const map = window.ProductConfiguratorMap;
    if (!featureId || !variantId || !map) return false;

    const configurator = document.querySelector('[data-ar-configurator]');
    const alias = configurator && map.variantIdAliasBySku[configurator.getAttribute('sku')];
    if (alias && alias[variantId]) variantId = alias[variantId];

    const valuesByText = map.variantIdByOptionValue[featureId];
    if (!valuesByText) return false;

    // More than one key can map to the same variant ID (typos/aliases kept
    // alongside the correct spelling - eg. "monitor" has both "citirow
    // connect touchscreen" and "cityrow connect touchscreen" mapping to
    // "city-row", confirmed live 2026-09-07 to be a genuine typo, not a
    // real product's actual option text). Taking only the first match here
    // used to silently fail whenever that first key wasn't the one this
    // product's own rendered option text actually uses - try every
    // candidate against the DOM rather than assume key order is meaningful.
    const hiddenOption = Object.keys(valuesByText)
      .filter((text) => valuesByText[text] === variantId)
      .map((text) => document.querySelector(`[data-hidden-option-value="${text}"]`))
      .find(Boolean);
    if (!hiddenOption) return false;

    if (!hiddenOption.checked) {
      hiddenOption.checked = true;
      hiddenOption.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return true;
  }

  function syncHiddenVariantOption(input) {
    const featureId = (input.dataset.ldFeatureId || '').trim();
    const variantId = (input.dataset.ldVariantId || '').trim();
    return applyHiddenVariantOption(featureId, variantId);
  }

  // Inverse of syncHiddenVariantOption: an allowMultiple drawer (eg.
  // Accessories, now that Rail Length lives there) has no "the other radio
  // got picked instead" moment the way a single-select drawer does -
  // unchecking "XL Rails" is the only signal that Rail Length's override
  // just went away, so without this the real Shopify variant it swapped in
  // would stay pointed at XL Rails forever, even once nothing checked is
  // claiming that feature any more. Falls back to
  // product-3d-configurator-map.js's own scene default for the feature -
  // the same default the 3D sync already assumes for accessory features
  // with no drawer pick (see accessoryFeatureIds there).
  function revertHiddenVariantOption(featureId) {
    const map = window.ProductConfiguratorMap;
    const defaultVariantId = map && map.defaultVariantByFeature[featureId];
    if (defaultVariantId) applyHiddenVariantOption(featureId, defaultVariantId);
  }

  // Inverse of syncHiddenVariantOption(): a single-select drawer's own
  // "checked" default is always sections/main-product.liquid's forloop.first
  // (there's no Liquid concept of "the real current variant's Performance
  // Tech pick" at render time), regardless of what product.selected_or_
  // first_available_variant actually resolved to - including via a
  // ?variant= URL param, which Shopify resolves correctly server-side.
  // Without this, initOptionDrawer's startup sync always forces that
  // forloop.first default onto the real hidden Shopify option, silently
  // discarding a deep-linked Performance Tech variant on every page load.
  // Finds which of this drawer's own inputs corresponds to whichever
  // hidden option value is ALREADY checked (server-rendered from the real
  // current variant), so that can be reconciled onto the drawer before its
  // own default gets force-synced over it.
  function findInputMatchingCurrentVariant(list) {
    const map = window.ProductConfiguratorMap;
    if (!map) return null;

    const configurator = document.querySelector('[data-ar-configurator]');
    const alias = configurator && map.variantIdAliasBySku[configurator.getAttribute('sku')];

    return (
      Array.from(list.querySelectorAll('input[data-ld-feature-id]')).find((input) => {
        const featureId = (input.dataset.ldFeatureId || '').trim();
        let variantId = (input.dataset.ldVariantId || '').trim();
        if (!featureId || !variantId) return false;
        if (alias && alias[variantId]) variantId = alias[variantId];

        const valuesByText = map.variantIdByOptionValue[featureId];
        if (!valuesByText) return false;

        return Object.keys(valuesByText)
          .filter((text) => valuesByText[text] === variantId)
          .some((text) => document.querySelector(`[data-hidden-option-value="${text}"]`)?.checked);
      }) || null
    );
  }

  function getOrCreateDrawer(drawerId) {
    let drawer = document.getElementById(`pdp-drawer-instance-${drawerId}`);
    if (drawer) return drawer;

    const template = document.querySelector(`template[data-pdp-drawer="${drawerId}"]`);
    if (!template) return null;

    drawer = template.content.firstElementChild.cloneNode(true);
    drawer.id = `pdp-drawer-instance-${drawerId}`;
    document.body.append(drawer);
    initOptionDrawer(drawer);
    return drawer;
  }

  function initOptionDrawer(drawer) {
    const list = drawer.querySelector('[data-pdp-option-drawer]');
    if (!list) return;

    const blockId = list.dataset.pdpOptionDrawer;
    const allowMultiple = list.dataset.allowMultiple === 'true';
    const labelNone = list.dataset.labelNone || 'None selected';
    const labelIncluded = list.dataset.labelIncluded || 'Included';
    const summaryTarget = list.dataset.summaryTarget
      ? document.querySelector(list.dataset.summaryTarget)
      : null;
    const priceTarget = list.dataset.priceTarget
      ? document.querySelector(list.dataset.priceTarget)
      : null;
    const card = document.querySelector(`[data-pdp-option-card="${blockId}"]`);
    const cardTitle = card ? card.querySelector('[data-pdp-option-card-title]') : null;
    const cardPrice = card ? card.querySelector('[data-pdp-option-card-price]') : null;

    // Tracks which scene features this drawer's own checked items are
    // currently claiming (see revertHiddenVariantOption above) - the only
    // way to know a feature just stopped being claimed is comparing against
    // what it used to be a moment ago, so this has to persist between calls
    // rather than being derived fresh each time.
    let activeFeatureIds = new Set();

    // update() also runs once automatically the moment a drawer is created
    // (see the bottom of this function) purely to sync the running price
    // total against whatever's already checked - that's not the shopper
    // making a choice, so it must never collapse the card. Only a real
    // 'change' event (the shopper actually picking something) does that,
    // via the isUserChange argument below.
    function update(isUserChange) {
      const checked = Array.from(list.querySelectorAll('input:checked'));

      // Does THIS input actually have a real Shopify variant for the pick?
      // If so its price already lives on the variant once
      // syncHiddenVariantOption's dispatched 'change' resolves, and it must
      // be excluded from the accessory-price rollup / cart-add below - see
      // updateMainPrice()'s comment for the £250 PowerPulley double-charge
      // this prevents. Checked per item rather than once for the whole
      // list, because an allowMultiple drawer (eg. Accessories) can mix
      // plain accessories with no ld_feature_id (Grips, Floor Mat) alongside
      // a feature-backed pick (Rail Length) in the very same list.
      const currentFeatureIds = new Set();
      const items = checked.map((input) => {
        const priceCarriedByVariant = syncHiddenVariantOption(input);
        const ldFeatureId = (input.dataset.ldFeatureId || '').trim();
        if (priceCarriedByVariant) currentFeatureIds.add(ldFeatureId);
        return {
          id: input.value,
          title: input.dataset.optionTitle,
          price: input.dataset.optionPrice,
          priceCents: Number(input.dataset.optionPriceCents) || 0,
          priceCarriedByVariant,
          // Carried into the ?upsell= URL (see updateURL/restoreFromURL) so
          // a colour-matched duplicate product (eg. "CityRow Connect
          // Touchscreen Shadow" vs the plain "CityRow Connect Touchscreen"
          // on Oak - same scene pick, deliberately different real product
          // per wood colour) can still be found on whatever wood-colour
          // page the link/swatch-navigation lands on, even though its own
          // Shopify id never matches the one the link was generated with.
          ldFeatureId,
          ldVariantId: (input.dataset.ldVariantId || '').trim(),
        };
      });

      // Single-select drawers always have exactly one radio checked, so a
      // feature there is only ever reclaimed by picking the other radio -
      // this only ever finds something to do in an allowMultiple drawer,
      // where unchecking a box is the only signal a feature's override just
      // went away.
      activeFeatureIds.forEach((featureId) => {
        if (!currentFeatureIds.has(featureId)) revertHiddenVariantOption(featureId);
      });
      activeFeatureIds = currentFeatureIds;

      selectedUpsells[blockId] = items;

      updateMainPrice();
      updateProductTitle();
      updateURL();

      if (checked.length === 0) {
        if (summaryTarget) summaryTarget.textContent = labelNone;
        if (card) card.hidden = true;
        return;
      }

      if (summaryTarget) {
        // One element per title (not a comma-joined string) so the [data-pdp-option-summary]
        // column-list CSS can lay each selection out on its own line.
        summaryTarget.replaceChildren(
          ...checked.map((input) => {
            const item = document.createElement('span');
            const priceText = input.dataset.optionPriceCents === '0'
              ? labelIncluded
              : `+${input.dataset.optionPrice}`;
            item.textContent = `${input.dataset.optionTitle} (${priceText})`;
            return item;
          })
        );
      }

      if (!allowMultiple && card) {
        const selected = checked[0];
        const selectedIsFree = selected.dataset.optionPriceCents === '0';
        card.hidden = false;
        if (priceTarget) priceTarget.textContent = selectedIsFree ? labelIncluded : `+(${selected.dataset.optionPrice})`;

        if (isUserChange) {
          // A real pick was made - collapse to the compact summary and stay
          // that way for the rest of the session, even through a later
          // reopen/close that doesn't change anything.
          card.classList.add('pdp-option-card--collapsed');
          if (cardTitle) cardTitle.textContent = selected.dataset.optionTitle;
          if (cardPrice) cardPrice.textContent = selectedIsFree ? labelIncluded : `+${selected.dataset.optionPrice}`;
        }
      }
    }

    // Two or more items in the same allowMultiple drawer can share an
    // ld_feature_id (eg. Standard Rails / XL Rails both claim "rails") - the
    // scene, and the real Shopify variant syncHiddenVariantOption swaps in
    // for it, can only hold one value per feature, so checking one here
    // must uncheck any other checked item claiming that same feature.
    // Single-select drawers already get this for free from native
    // radio-group behaviour (the browser unchecks the other radio before
    // this even fires), so this only ever finds anything to do in an
    // allowMultiple list. Registered before the update(true) listener below
    // so update() always sees the corrected checked set.
    list.addEventListener('change', (event) => {
      const input = event.target;
      const featureId = (input.dataset.ldFeatureId || '').trim();
      if (!input.checked || !featureId) return;
      list.querySelectorAll('input[data-ld-feature-id]').forEach((other) => {
        if (other !== input && other.checked && (other.dataset.ldFeatureId || '').trim() === featureId) {
          other.checked = false;
        }
      });
    });

    list.addEventListener('change', () => update(true));

    // Reconcile against the real current variant BEFORE the eager
    // materialization below forces this drawer's Liquid-rendered default
    // onto the actual hidden Shopify option (see findInputMatchingCurrentVariant).
    // A real 'change' event (not a direct .checked flip) so this goes through
    // the exact same isUserChange path as a genuine pick - collapsing the
    // static card and updating its title/price to match, the same way
    // restoreFromURL() already does for the "Choose Accessories" case, rather
    // than leaving the visible card showing the wrong Performance Tech option
    // while the real variant underneath is actually correct.
    if (!allowMultiple) {
      const restored = findInputMatchingCurrentVariant(list);
      if (restored && !restored.checked) {
        restored.checked = true;
        restored.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    update(false);
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-js-open-drawer]');
    if (opener) {
      const drawer = getOrCreateDrawer(opener.dataset.jsOpenDrawer);
      if (drawer) drawer.show();
      return;
    }

    const selector = e.target.closest('[data-js-select-input]');
    if (selector) {
      const input = document.getElementById(selector.dataset.jsSelectInput);
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const drawer = selector.closest('sidebar-drawer');
      if (drawer) drawer.hide();
      return;
    }

    const openPdpDrawers = Array.from(document.querySelectorAll('sidebar-drawer.sidebar--opened')).filter(
      (drawer) => drawer.id.startsWith('pdp-drawer-instance-')
    );
    const topDrawer = openPdpDrawers[openPdpDrawers.length - 1];
    if (topDrawer && !topDrawer.contains(e.target)) {
      topDrawer.hide();
    }
  });

  // Pure data - no fetch of its own. component-product-form.js folds these
  // into the SAME /cart/add.js request as the base rower (one combined
  // `items` array) rather than a separate follow-up POST, so the whole add
  // is genuinely atomic: if any accessory is invalid/out of stock, Shopify
  // rejects the entire request and adds nothing at all - including the
  // base item - instead of the base succeeding while accessories silently
  // fail (or, worse, the reverse).
  //
  // priceCarriedByVariant items must not ALSO become a line item here -
  // see updateMainPrice()'s comment. Their pick is already reflected in
  // the base product's own variant (input[name="id"]), which the combined
  // request's first item already carries.
  function getSelectedUpsellItems() {
    return Object.values(selectedUpsells)
      .flat()
      .filter((item) => !item.priceCarriedByVariant)
      .map((item) => ({ id: item.id, quantity: 1 }));
  }

  // Changing any *visible* variant option (Wood Type, Rail Length) re-renders
  // the price blocks from fresh server HTML (component-product-form.js
  // #renderProductUpdates) — that HTML only knows the bare variant price, so
  // it wipes out whatever accessory surcharge updateMainPrice() had added on
  // top. krown:variant:change fires right after that re-render, so re-running
  // updateMainPrice() here re-applies the surcharge without touching anything
  // else (the drawer's own checked state and the 3D scene are unaffected by
  // this — only the on-page price DOM gets overwritten by the variant swap).
  document.addEventListener('krown:variant:change', updateMainPrice);
  document.addEventListener('krown:variant:change', updateProductTitle);

  // restoreFromURL() MUST run before the eager materialization below. Each
  // drawer's own update(false) call (see initOptionDrawer) unconditionally
  // calls updateURL(), which rewrites the page's OWN ?upsell= query string
  // from whatever's currently checked - on first init, that's always the
  // block's Liquid-rendered default, not yet whatever a shared link asked
  // for. Materializing every drawer first (in the original order) clobbered
  // a shared ?upsell= link with the defaults' own ids before restoreFromURL
  // ever got to read it - confirmed live 2026-09-09: a link with a real
  // Performance Tech + Accessory pick reverted to "S4 Bluetooth Monitor" /
  // "None" the moment it was opened in a fresh browser, and the URL itself
  // was rewritten to just the default's id in the process. restoreFromURL()
  // reads and destructures window.location.search into local values *before*
  // calling getOrCreateDrawer for anything, so running it first is enough to
  // read the real, unmodified link - getOrCreateDrawer is idempotent, so the
  // materialization loop below still runs for every drawer afterward without
  // re-initializing (and so without reverting) whichever ones restoreFromURL
  // already created and correctly restored. restoreFromReadableParams()
  // reads window.location.search itself too (tech=/rails=, not upsell=) and
  // shares the exact same "must run before materialization" requirement -
  // grouped with restoreFromURL() here for that reason, order between the
  // two doesn't otherwise matter since they read disjoint query params.
  restoreFromReadableParams();
  restoreFromURL();

  // Materialize every remaining pdp_option_drawer so a single-select block's
  // forced-checked default (sections/main-product.liquid sets `checked` on
  // forloop.first) lands in selectedUpsells from page load, not only once a
  // shopper physically opens the drawer. Without this, a shopper who never
  // opens "Choose Tech" - because the default is already shown on the static
  // pdp-option-card - and goes straight to Add to Cart gets none of the
  // upsell's price added, even though the page displays it. getOrCreateDrawer
  // is the same path a real click uses and is idempotent, so a later open
  // just reuses this instance rather than re-initializing it.
  document.querySelectorAll('template[data-pdp-drawer]').forEach((template) => {
    getOrCreateDrawer(template.dataset.pdpDrawer);
  });

  return { getSelectedUpsellItems };
})();

